import { createServerFn } from '@tanstack/react-start';

import type { SettingsRoles } from './permissions';

import { loadSettingsActor } from './settings-access.server';

/**
 * What organization and team settings pages need to know about the active
 * workspace: names, which features the organization has, and the signed-in
 * researcher's roles (for hiding what they cannot do; server functions check
 * again before every change).
 */
export interface WorkspaceSettingsContext {
  organization: {
    id: string;
    name: string;
    ssoEnabled: boolean;
    hasEnabledSsoProvider: boolean;
    participantDatabase: boolean;
    privateTeams: boolean;
  };
  team: { id: string; name: string } | null;
  roles: SettingsRoles;
}

export const getWorkspaceSettingsContext = createServerFn().handler(
  async (): Promise<WorkspaceSettingsContext> => {
    const actor = await loadSettingsActor();
    const { organization, team } = actor;
    return {
      organization: {
        id: actor.organizationId,
        name: organization.name ?? '',
        ssoEnabled: organization.features?.ssoEnabled ?? false,
        hasEnabledSsoProvider:
          organization.ssoProviders?.some((p) => p.enabled === true) ?? false,
        participantDatabase:
          organization.features?.participantDatabase ?? false,
        privateTeams: organization.privateTeams ?? false,
      },
      team:
        team && actor.teamId
          ? { id: actor.teamId, name: team.name ?? '' }
          : null,
      roles: actor.roles,
    };
  },
);
